import DoneIcon from '@mui/icons-material/Done'
import { Button, Grid, Typography } from '@mui/material'
import { CardActionConfirmDialog } from '~/features/adv_account/CardActionConfirmDialog'
import { useRouter } from 'next/router'
import { snackActions } from '~/lib/snackbarHelper'
import React, { useState } from 'react'
import { deleteCard, setDefaultCard } from '~/api/PaymentRepository'
import { PaymentMethodKind, type Card, type CardList } from '~/lib/types/AdvAccount'
import { pagesPath } from '~/lib/$path'
import { logger } from '~/lib/logger'

type cardActionProps = {
  card: Card
  cardList: CardList | undefined
  paymentMethod: PaymentMethodKind | null
  fetchData: () => Promise<void>
}

export const CardAction = ({ card, cardList, paymentMethod, fetchData }: cardActionProps) => {
  const [openDefaultSetDialog, setOpenDefaultSetDialog] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const router = useRouter()

  const canChangeDefaultCard = paymentMethod !== 'credit_card_gmopg'

  async function setDefaultCardSubmit(cardId: string) {
    await setDefaultCard(cardId)
      .then(() => {
        snackActions.success('メインカードに設定しました')
        fetchData()
          .then(() => {
            handleSetDefault(false)
            router.push(pagesPath.adv_account.credit_registration.$url())
          })
          .catch((e) => {
            console.log(e)
          })
      })
      .catch((e) => {
        snackActions.warning('メインカードの設定に失敗しました')
        logger.error(new Error('Failed to set default card', { cause: e }))
      })
  }

  async function deleteCardSubmit(cardId: string) {
    await deleteCard(cardId)
      .then(() => {
        snackActions.success('カードを削除しました')
        fetchData()
          .then(() => {
            router.push(pagesPath.adv_account.credit_registration.$url())
          })
          .catch((e) => {
            console.log(e)
          })
      })
      .catch((e) => {
        snackActions.warning('カードの削除に失敗しました')
        logger.error(new Error('Failed to delete card', { cause: e }))
      })
  }

  const handleSetDefault = (status: boolean) => {
    setOpenDefaultSetDialog(status)
  }

  const handleDelete = (status: boolean) => {
    setOpenDeleteDialog(status)
  }

  if (cardList?.defaultCard === card.id) {
    return (
      <Typography
        sx={{
          color: 'primary.main',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <DoneIcon />
        メインカードに設定されています。
        <br />
        各種請求はこちらのカードで決済されます。
      </Typography>
    )
  }

  return (
    <Grid container justifyContent="flex-start" alignItems="center" spacing={2}>
      {canChangeDefaultCard && (
        <Grid>
          <Button
            color="primary"
            variant="outlined"
            style={{ fontSize: '1em' }}
            onClick={() => {
              handleSetDefault(true)
            }}
          >
            メインカードにする
          </Button>
        </Grid>
      )}
      <Grid>
        <Button
          variant="outlined"
          color="error"
          onClick={() => {
            handleDelete(true)
          }}
        >
          カードを削除する
        </Button>
      </Grid>
      {!canChangeDefaultCard && (
        <Grid>
          <Typography
            sx={{
              color: 'primary.main',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            こちらのカードにメインカードを変更する場合は、一度削除して再度追加してください。
          </Typography>
        </Grid>
      )}
      <CardActionConfirmDialog
        card={card}
        open={openDefaultSetDialog}
        onClose={handleSetDefault}
        onOk={setDefaultCardSubmit}
        actionText="メインカードに設定"
      />
      <CardActionConfirmDialog
        card={card}
        open={openDeleteDialog}
        onClose={handleDelete}
        onOk={deleteCardSubmit}
        actionText="カードの削除"
      />
    </Grid>
  )
}
